import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Config from '@skin_config';
import { useTranslation } from '@common/hooks/translation';
import Timer from '@common/components/timer';
import { PopupContext } from '@app/dev/contexts/popup';
import PaymentsService from '@app/dev/services/payments';
import { TerminalDepositModesEnum } from '@app/dev/constants/enums/terminalDepositModes';
import { formatMoneyAmount } from '@common/helpers/amount';
import { GetCurrencyConfig } from '@common/helpers/selectors';
import { waitForRegularCashoutResponse } from '@app/dev/helpers/cashout';
import { POPUPS } from '@common/constants/popups';
import LoaderButton from '@common/components/loaderButton';

import { useBalance } from '../../hooks/balance';
import { CashoutMoney, PartialCashoutMoney } from '../../../actions/terminal';
import CashoutPartialAmount from './partialAmount';

import OctopayLogo from '../../../images/okto.png';

import './style.scss';


const baseText = 'Do you wish to cash out';
const partialFullText = 'the full amount or a partial amount';
const oktoText = 'to your OktoPay account';

const getCashoutDescription = (partialEnabled, isPartialMode, depositMode) => {
  if (isPartialMode) return 'Please input the cash out amount';

  const isOktoPay = depositMode === TerminalDepositModesEnum.Octo;

  const textParts = [baseText, partialEnabled && ` ${partialFullText}`, isOktoPay && ` ${oktoText}`, '?'].filter(Boolean);

  return textParts.join('');
};

function Cashout({ clearStateBeforeCashout }) {
  const t = useTranslation();
  const dispatch = useDispatch();
  const [{ openPopup, closePopup }] = useContext(PopupContext);
  const [loading, setLoading] = useState(false);
  const [isPartialMode, setIsPartialMode] = useState(false);
  const [partialAmount, setPartialAmount] = useState('');
  const [partialAmountErrorMessage, setPartialAmountErrorMessage] = useState('');
  const { playerInfo, isAdminLoggedIn, depositMode } = useSelector((state) => state.terminal);
  const currencyConfig = useSelector((state) => (
    GetCurrencyConfig(state, Config.currencySymbolRounding)
  ));

  const balance = useBalance();

  const needAdminForCashout = Config.main.needAdminConfirmation && !isAdminLoggedIn;

  const isOctoPay = depositMode === TerminalDepositModesEnum.Octo;

  const changePartialAmount = (value) => {
    setPartialAmount(value);

    if (isPartialMode && Number(value) > balance) setPartialAmountErrorMessage('You don\'t have enough funds');
    else if (partialAmountErrorMessage) setPartialAmountErrorMessage('');
  };

  const onCashout = async () => {
    if (loading) return;

    if (isPartialMode && Number.isNaN(Number(partialAmount))) return;

    if (isPartialMode && Number(partialAmount) > balance) {
      setPartialAmountErrorMessage('You don\'t have enough funds');
      return;
    }

    const needDelayBeforeCashout = playerInfo?.PersonalId && clearStateBeforeCashout;

    if (needDelayBeforeCashout) {
      await PaymentsService.clearSessionBalanceState();
    }

    setTimeout(
      async () => {
        const action = isPartialMode
          ? PartialCashoutMoney({ amount: Number(partialAmount) })
          : CashoutMoney(null);

        dispatch(action);

        if (isOctoPay) closePopup();
        else {
          setLoading(true);
          const response = await waitForRegularCashoutResponse();
          setLoading(false);

          if (response.success) {
            openPopup(POPUPS.INFO, {
              icon: 'icon-circle-done static-green',
              title: 'Cash out successful!',
              buttonText: '',
              confirmButtonText: 'Ok',
              className: 'cashout-success-popup-content',
              subtitle: [
                'Cash out amount: {1}',
                <span key={1} className="color-hero">
                  {formatMoneyAmount(
                    isPartialMode ? Number(partialAmount) : balance,
                    Config,
                    currencyConfig,
                  )}
                </span>,
              ],
            });
          }
        }
      },
      needDelayBeforeCashout ? 1800 : 200,
    );
  };

  return (
    <div className="popup-content popup-small cashout-main-popup">
      <i onClick={closePopup} className="global-icon icon-Close close-popup" />
      <h3 className="popup-title">{t('Cashout')}</h3>
      <div className="popup-main-content">
        <p className="text-center">
          {t.jsx('Your current balance is {1}', <span className="color-hero">{formatMoneyAmount(balance, Config, currencyConfig)}</span>)}
        </p>
        <p className="text-center">{t(getCashoutDescription(Config.enablePartialCashout, isPartialMode, depositMode))}</p>
        {
          isOctoPay
            ? <img src={OctopayLogo} className="cashout-icon-okto" alt="okto logo" />
            : <i className="global-icon icon-cashout cashout-icon" />
        }
        <Timer onFinish={closePopup} duration={Config.main.cashoutPopupTimer} variant={isOctoPay ? 'oktopay-timer' : ''} />
        {needAdminForCashout && <p className="admin-confirm-text">{t('Please ask administrator for confirmation')}</p>}
      </div>
      {isPartialMode
        ? (
          <>
            <CashoutPartialAmount
              amount={partialAmount}
              onChange={changePartialAmount}
              errorMessage={partialAmountErrorMessage}
            />

            <div className="popup-actions">
              <LoaderButton
                type="button"
                onClick={onCashout}
                className={`popup-confirm-button ${isOctoPay ? 'oktopay-cashout' : ''}`}
                loading={loading}
                disabled={
                  !Number(partialAmount) || needAdminForCashout || !!partialAmountErrorMessage
                }
              >
                {t('Cash out')}
              </LoaderButton>
            </div>
          </>
        ) : (
          <div className="popup-actions">
            {Config.enablePartialCashout && (
              <button type="button" onClick={() => setIsPartialMode(true)} className="popup-cancel-button">
                {t('Enter amount')}
              </button>
            )}

            <LoaderButton
              type="button"
              onClick={onCashout}
              disabled={needAdminForCashout}
              className={`popup-confirm-button ${isOctoPay ? 'oktopay-cashout' : ''}`}
              loading={loading}
            >
              {t(Config.enablePartialCashout ? 'Cash out full' : 'Cash out')}
            </LoaderButton>
          </div>
        )}
    </div>
  );
}

export default Cashout;
